import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguageContext } from '../contexts/LanguageContext';
import { CircularProgress } from '@mui/material';

type EntitlementDto = {
  id: string;
  subjectType: 'product' | 'collection';
  subjectId: string;
  status: 'active' | 'revoked';
  createdAt: string;
  license: null | {
    id: string;
    displayLast4: string;
    status: 'active' | 'revoked' | 'suspended';
    policy: { expiresAt: string | null };
  };
};

type Plugin = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  version?: string;
};

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MyProducts: React.FC = () => {
  const { t } = useLanguageContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entitlements, setEntitlements] = useState<EntitlementDto[]>([]);
  const [plugins, setPlugins] = useState<Plugin[]>([]);

  useEffect(() => {
    let isCancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [entsRes, pluginsRes] = await Promise.all([
          axios.get<{ entitlements: EntitlementDto[] }>(`/api/account/entitlements`, { headers: getAuthHeaders() }),
          axios.get<{ success?: boolean; data?: Plugin[]; count?: number }>(`/api/plugins`),
        ]);

        if (isCancelled) return;
        const ents = entsRes.data.entitlements || [];
        const plist = (pluginsRes.data.data ?? []) as Plugin[];
        setEntitlements(ents);
        setPlugins(plist);
      } catch (e: any) {
        if (isCancelled) return;
        setError(e?.response?.data?.error || 'Failed to load your products');
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };
    load();
    return () => {
      isCancelled = true;
    };
  }, []);

  const productById = useMemo(() => {
    const map = new Map<string, Plugin>();
    for (const p of plugins) map.set(p._id, p);
    return map;
  }, [plugins]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#a855f7' }} />
          <p className="text-white mt-4">{t('products.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-900/40 border border-red-700 rounded p-4 text-red-200">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-white mb-6">{t('profile.my_products') || 'My Products'}</h1>
      {entitlements.length === 0 ? (
        <p className="text-gray-300">{t('profile.no_entitlements') || 'You do not have any products yet.'}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {entitlements.map((e) => {
            const p = e.subjectType === 'product' ? productById.get(e.subjectId) : undefined;
            return (
              <div key={e.id} className="bg-slate-800/70 rounded-lg border border-slate-700 overflow-hidden">
                {p?.imageUrl && (
                  <div className="h-40 bg-slate-900 flex items-center justify-center overflow-hidden">
                    <img src={p.imageUrl} alt={p.name} className="object-cover w-full h-full" />
                  </div>
                )}
                <div className="p-4">
                  <h2 className="text-xl font-semibold text-white mb-1">{p?.name || e.subjectId}</h2>
                  <p className="text-gray-400 text-sm mb-3">
                    {p?.version ? `${t('common.version') || 'Version'} ${p.version}` : ''}
                  </p>
                  <div className="flex items-center justify-between text-gray-200">
                    <div>
                      <div className="text-sm">
                        {t('profile.license_last4') || 'License'}: **** **** **** {e.license?.displayLast4 || '----'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {t('common.status') || 'Status'}: {e.license?.status || e.status}
                      </div>
                    </div>
                    {p?.slug && (
                      <a
                        href={`/plugins/${p.slug}`}
                        className="inline-flex items-center px-3 py-1.5 rounded bg-purple-600 hover:bg-purple-500 text-white text-sm"
                      >
                        {t('common.view') || 'View'}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyProducts;


